import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { BibAssign } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputInt,
    ValidatedInt,
    InputSelect,
    DataSourceLookup,
    InputNullableDateTime,
    DateComp,
    InputBool,
    DisplayBool,
} from '@/components';
import { useEventCommonStore, useContestantStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class BibAssignGuiDef extends GuiDef<BibAssign> {
    constructor() {
        super('BibAssign');

        const EventCommon = storeToRefs(useEventCommonStore());
        const CategoriesWithAllSource = computed(() => {
            return EventCommon.CategoriesWithAllAsSelectLine.value;
        });
        const GroupsWithAllSource = computed(() => {
            return EventCommon.GroupsWithAllAsSelectLine.value;
        });
        const Contestant = storeToRefs(useContestantStore());
        const ContestantFullNameWithEmptySource = computed(() => {
            return Contestant.ContestantFullNameWithEmptyAsSelectLine.value;
        });

        const Bib = new FieldDef<BibAssign>();
        Bib.Name = 'Bib';
        Bib.FieldName = 'Bib';
        Bib.DefinedFor = 'Both';
        Bib.TranslationKey = 'bibassign.bib';
        Bib.Editable = true;
        Bib.Filterable = false;
        Bib.Sortable = true;
        Bib.CssClasses = '';
        Bib.value = (bibassign) => bibassign.Bib;
        Bib.setValue = (bibassign, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            bibassign.Bib = value;
        };
        Bib.DetailEditor = markRaw(InputInt);
        Bib.DetailEditorProps = {};
        Bib.CellEditor = markRaw(InputInt);
        Bib.CellEditorProps = {};
        Bib.CellRenderer = markRaw(ValidatedInt);
        Bib.CellRendererProps = {};
        Bib.Validator = null;
        Bib.SortDirection = undefined;
        Bib.cellClassRules = undefined;
        Bib.MinWidth = undefined;
        Bib.MaxWidth = undefined;
        Bib.Tooltip = null;
        this.Fields.push(Bib);

        const CategoryID = new FieldDef<BibAssign>();
        CategoryID.Name = 'CategoryID';
        CategoryID.FieldName = 'CategoryID';
        CategoryID.DefinedFor = 'Both';
        CategoryID.TranslationKey = 'bibassign.categoryid';
        CategoryID.Editable = true;
        CategoryID.Filterable = false;
        CategoryID.Sortable = true;
        CategoryID.CssClasses = '';
        CategoryID.value = (bibassign) => bibassign.CategoryID;
        CategoryID.setValue = (bibassign, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            bibassign.CategoryID = value;
        };
        CategoryID.DetailEditor = markRaw(InputSelect);
        CategoryID.DetailEditorProps = { datasource: { mapsource: CategoriesWithAllSource } };
        CategoryID.CellEditor = markRaw(InputSelect);
        CategoryID.CellEditorProps = { datasource: { mapsource: CategoriesWithAllSource } };
        // @ts-expect-error @ts-ignore
        CategoryID.CellRenderer = markRaw(DataSourceLookup);
        CategoryID.CellRendererProps = { datasource: { mapsource: CategoriesWithAllSource } };
        CategoryID.ExportValueFormatter = (obj: BibAssign) => {
            return CategoriesWithAllSource.value.get(obj.CategoryID)?.text;
        };
        CategoryID.Validator = null;
        CategoryID.SortDirection = undefined;
        CategoryID.cellClassRules = undefined;
        CategoryID.MinWidth = undefined;
        CategoryID.MaxWidth = undefined;
        CategoryID.Tooltip = null;
        this.Fields.push(CategoryID);

        const GroupID = new FieldDef<BibAssign>();
        GroupID.Name = 'GroupID';
        GroupID.FieldName = 'GroupID';
        GroupID.DefinedFor = 'Both';
        GroupID.TranslationKey = 'bibassign.groupid';
        GroupID.Editable = true;
        GroupID.Filterable = false;
        GroupID.Sortable = true;
        GroupID.CssClasses = '';
        GroupID.value = (bibassign) => bibassign.GroupID;
        GroupID.setValue = (bibassign, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            bibassign.GroupID = value;
        };
        GroupID.DetailEditor = markRaw(InputSelect);
        GroupID.DetailEditorProps = { datasource: { mapsource: GroupsWithAllSource } };
        GroupID.CellEditor = markRaw(InputSelect);
        GroupID.CellEditorProps = { datasource: { mapsource: GroupsWithAllSource } };
        // @ts-expect-error @ts-ignore
        GroupID.CellRenderer = markRaw(DataSourceLookup);
        GroupID.CellRendererProps = { datasource: { mapsource: GroupsWithAllSource } };
        GroupID.ExportValueFormatter = (obj: BibAssign) => {
            return GroupsWithAllSource.value.get(obj.GroupID)?.text;
        };
        GroupID.Validator = null;
        GroupID.SortDirection = undefined;
        GroupID.cellClassRules = undefined;
        GroupID.MinWidth = undefined;
        GroupID.MaxWidth = undefined;
        GroupID.Tooltip = null;
        this.Fields.push(GroupID);

        const StartTime = new FieldDef<BibAssign>();
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Both';
        StartTime.TranslationKey = 'bibassign.starttime';
        StartTime.Editable = true;
        StartTime.Filterable = true;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (bibassign) => bibassign.StartTime;
        StartTime.setValue = (bibassign, value) => (bibassign.StartTime = value);
        StartTime.DetailEditor = markRaw(InputNullableDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellEditor = markRaw(InputNullableDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType };
        StartTime.Validator = null;
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = undefined;
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const ContestantID = new FieldDef<BibAssign>();
        ContestantID.Name = 'ContestantID';
        ContestantID.FieldName = 'ContestantID';
        ContestantID.DefinedFor = 'Both';
        ContestantID.TranslationKey = 'bibassign.contestantid';
        ContestantID.Editable = false;
        ContestantID.Filterable = false;
        ContestantID.Sortable = true;
        ContestantID.CssClasses = '';
        ContestantID.value = (bibassign) => bibassign.ContestantID;
        ContestantID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        ContestantID.CellRenderer = markRaw(DataSourceLookup);
        ContestantID.CellRendererProps = { datasource: { mapsource: ContestantFullNameWithEmptySource }, readonly: true };
        ContestantID.ExportValueFormatter = (obj: BibAssign) => {
            return ContestantFullNameWithEmptySource.value.get(obj.ContestantID)?.text;
        };
        ContestantID.Validator = null;
        ContestantID.SortDirection = undefined;
        ContestantID.cellClassRules = undefined;
        ContestantID.MinWidth = undefined;
        ContestantID.MaxWidth = undefined;
        ContestantID.Tooltip = null;
        this.Fields.push(ContestantID);

        const Blocked = new FieldDef<BibAssign>();
        Blocked.Name = 'Blocked';
        Blocked.FieldName = 'Blocked';
        Blocked.DefinedFor = 'Both';
        Blocked.TranslationKey = 'bibassign.blocked';
        Blocked.Editable = true;
        Blocked.Filterable = false;
        Blocked.Sortable = true;
        Blocked.CssClasses = '';
        Blocked.value = (bibassign) => bibassign.Blocked;
        Blocked.setValue = (bibassign, value) => (bibassign.Blocked = value);
        Blocked.DetailEditor = markRaw(InputBool);
        Blocked.DetailEditorProps = { label: 'bibassign.blocked' };
        Blocked.CellEditor = markRaw(InputBool);
        Blocked.CellEditorProps = {};
        Blocked.CellRenderer = markRaw(DisplayBool);
        Blocked.CellRendererProps = {};
        Blocked.Validator = null;
        Blocked.SortDirection = undefined;
        Blocked.cellClassRules = undefined;
        Blocked.MinWidth = undefined;
        Blocked.MaxWidth = undefined;
        Blocked.Tooltip = null;
        this.Fields.push(Blocked);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Categories']);
        await useEventCommonStore().EnsureLoaded(['Groups']);
        await useContestantStore().EnsureLoaded(['Contestants']);
    }
}
